import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import EmptyState from "../../../components/shared/EmptyState";
import LoadingState from "../../../components/shared/LoadingState";
import { toast } from "../../../components/shared/toast";

// /admin/approvals — approver inbox with status filter chips.
//
// Renders rows from GET /job-posting/approvals?status=... (server filters to
// approver_id = current JWT sub). Filter chips: Pending (default) · Approved ·
// Rejected · All — so admins can review their decision history, not just
// what's open.
const FILTERS = [
  { key: "PENDING",  label: "Pending"  },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
  { key: "SKIPPED",  label: "Skipped"  },
  { key: "ALL",      label: "All"      },
];

const statusChipColor = (s) => {
  switch ((s || "").toUpperCase()) {
    case "PENDING":  return "bg-amber-100 text-amber-800";
    case "APPROVED": return "bg-emerald-100 text-emerald-800";
    case "REJECTED": return "bg-red-100 text-red-800";
    case "SKIPPED":  return "bg-slate-100 text-slate-600";
    default:         return "bg-slate-100 text-slate-600";
  }
};

export default function MyApprovals() {
  const nav = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [delegatingId, setDelegatingId] = useState(null);
  const [delegateTo, setDelegateTo] = useState("");
  const [delegateNote, setDelegateNote] = useState("");

  const load = async (filter = statusFilter) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axiosInstance.get(`${baseUrl}/job-posting/approvals?status=${filter}`);
      setApprovals(Array.isArray(r.data?.approvals) ? r.data.approvals : []);
    } catch (e) { setError(e?.message || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { document.title = "My approvals - Ample Hire"; load(statusFilter); /* eslint-disable-next-line */ }, []);

  const switchFilter = (k) => { setStatusFilter(k); load(k); };

  const approve = async (a) => {
    setBusyId(a.id);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/approvals/${a.id}/approve`, {});
      toast.success(`Approved offer #${a.offerId}`);
      await load();
    } catch (e) { toast.error("Approve failed", { description: e?.data?.error || e?.message }); }
    finally { setBusyId(null); }
  };

  const reject = async (a) => {
    if (!rejectReason.trim()) { toast.warning("Reason required to reject"); return; }
    setBusyId(a.id);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/approvals/${a.id}/reject`, { reason: rejectReason });
      setRejectingId(null);
      setRejectReason("");
      toast.success(`Rejected offer #${a.offerId}`);
      await load();
    } catch (e) { toast.error("Reject failed", { description: e?.data?.error || e?.message }); }
    finally { setBusyId(null); }
  };

  const delegate = async (a) => {
    const uid = Number(delegateTo);
    if (!Number.isFinite(uid) || uid <= 0) {
      toast.warning("Delegate to user ID required");
      return;
    }
    setBusyId(a.id);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/approvals/${a.id}/delegate`, {
        delegateToUserId: uid,
        note: delegateNote || null,
      });
      setDelegatingId(null);
      setDelegateTo("");
      setDelegateNote("");
      toast.success(`Delegated offer #${a.offerId} to user ${uid}`);
      await load();
    } catch (e) { toast.error("Delegate failed", { description: e?.data?.error || e?.message }); }
    finally { setBusyId(null); }
  };

  return (
    <div className="page-enter">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-vf-base">My approvals</h1>
        <p className="text-sm text-vf-muted mt-1">Decisions on offers routed through approval chains.</p>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => switchFilter(f.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              statusFilter === f.key
                ? "bg-vf-primary text-white border-vf-primary"
                : "bg-white text-vf-base border-slate-300 hover:border-slate-400"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700 mb-4">{error}</div>}

      {loading && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <LoadingState variant="skeleton" shape="card" count={3} />
        </div>
      )}
      {!loading && approvals.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <EmptyState
            title={statusFilter === "PENDING" ? "Nothing waiting on you" : `No ${statusFilter.toLowerCase()} approvals`}
            description={
              statusFilter === "PENDING"
                ? "When an offer is routed through a chain that names you, it'll show up here."
                : `You don't have any ${statusFilter.toLowerCase()} approvals in your history yet.`
            }
          />
        </div>
      )}

      {!loading && approvals.length > 0 && (
        <div className="space-y-3">
          {approvals.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-vf-base">Offer #{a.offerId} · Step {a.stepOrder}</div>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusChipColor(a.status)}`}>
                      {a.status}
                    </span>
                  </div>
                  <div className="text-xs text-vf-muted mt-0.5">
                    Chain #{a.chainId} ·{" "}
                    {a.status === "PENDING"
                      ? `Notified ${a.notifiedAt ? new Date(a.notifiedAt).toLocaleString() : "—"}`
                      : a.decidedAt ? `Decided ${new Date(a.decidedAt).toLocaleString()}` : "Decided"}
                  </div>
                  {a.decisionNote && a.status !== "PENDING" && (
                    <div className="mt-1 text-[11px] text-vf-muted italic">"{a.decisionNote}"</div>
                  )}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => nav(`/admin/offers/${a.offerId}`)}
                          className="px-2 py-1 rounded border border-slate-300 text-xs">
                    View offer ↗
                  </button>
                  {a.status === "PENDING" && (
                    <>
                      <button disabled={busyId === a.id} onClick={() => approve(a)}
                              className="px-2 py-1 rounded bg-emerald-600 text-white text-xs disabled:opacity-50">
                        Approve
                      </button>
                      <button disabled={busyId === a.id} onClick={() => { setRejectingId(a.id); setRejectReason(""); }}
                              className="px-2 py-1 rounded border border-red-300 text-red-700 text-xs disabled:opacity-50">
                        Reject
                      </button>
                      <button disabled={busyId === a.id} onClick={() => { setDelegatingId(a.id); setDelegateTo(""); setDelegateNote(""); }}
                              className="px-2 py-1 rounded border border-slate-300 text-xs disabled:opacity-50">
                        Delegate
                      </button>
                    </>
                  )}
                </div>
              </div>
              {rejectingId === a.id && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                  <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                            rows={2} placeholder="Reason for rejection (required)"
                            className="w-full px-2 py-1 border border-red-300 rounded text-xs" />
                  <div className="flex gap-2 mt-2 justify-end">
                    <button onClick={() => { setRejectingId(null); setRejectReason(""); }}
                            className="px-2 py-1 rounded border border-slate-300 text-xs">Cancel</button>
                    <button onClick={() => reject(a)} disabled={!rejectReason.trim() || busyId === a.id}
                            className="px-2 py-1 rounded bg-red-600 text-white text-xs disabled:opacity-50">
                      Confirm reject
                    </button>
                  </div>
                </div>
              )}
              {delegatingId === a.id && (
                <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
                  <div>
                    <label className="block text-[11px] text-vf-muted mb-0.5">Delegate to user ID (required)</label>
                    <input type="number" value={delegateTo} onChange={(e) => setDelegateTo(e.target.value)}
                           placeholder="260"
                           className="w-32 px-2 py-1 border border-slate-300 rounded text-xs font-mono" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-vf-muted mb-0.5">Note (optional)</label>
                    <input type="text" value={delegateNote} onChange={(e) => setDelegateNote(e.target.value)}
                           placeholder="Why you're handing this off"
                           className="w-full px-2 py-1 border border-slate-300 rounded text-xs" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setDelegatingId(null); setDelegateTo(""); setDelegateNote(""); }}
                            className="px-2 py-1 rounded border border-slate-300 text-xs">Cancel</button>
                    <button onClick={() => delegate(a)} disabled={!delegateTo || busyId === a.id}
                            className="px-2 py-1 rounded bg-vf-primary text-white text-xs disabled:opacity-50">
                      Confirm delegate
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
